import dayjs from "dayjs";
import { warningToast } from "./toastHelper";
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_PAYLOAD_FORMAT } from "./constant";
import _ from "lodash";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

// constant for form name
export const addLaborClassificationTitleFormName = "Add Labor Classification Title";
export const listLaborClassificationTitleFormName = "Labor Classification Title List";
export const addLaborClassificationCategoryFormName = "Add Labor Classification Category";
export const listLaborClassificationCategoryFormName = "Labor Classification Category List";
export const listUserFormName = "Users List";
export const addUserFormName = "Add User";

export const employeeApprenticeNavigatorFormName = "Apprentice Navigator";
export const employeeNotesFormName = ["Add Notes", "Notes List"];
export const employeeDocumentsFormName = "Document List";
export const employeeProfileFormName = [
  "Basic Information",
  "Apprentice Information",
  "Address Information",
  "Mailing Address Inforamtion",
  "Additional Information"
];
export const employeeSocialMediaFormName = ["Add Social Media", "Social Media List"];
export const employeeTimeSheetFormName = "Time Sheets List";

export const partnersProfile = ["Partner Information", "Mailing Address", "Billing Address"];
export const partnersList = "Partners List";

export const projectJobsFormName = ["Add Job Detail or Job Detail", "Job List"];

export const fieldTruePermission = {
  canView: true,
  canAdd: true,
  canEdit: true
};

export const fieldDisabledPermission = {
  canView: true,
  canAdd: false,
  canEdit: false
};

export const prepareDefaultValues = fieldInfo => {
  const defaultValues = {};
  Object.keys(fieldInfo).forEach(field => {
    if (fieldInfo[field].defaultValue === undefined) defaultValues[field] = "";
    else defaultValues[field] = fieldInfo[field].defaultValue;
  });
  return defaultValues;
};

export const prepareInitialConfig = (fieldInfo, mode = "read") => {
  const initialConfig = {};
  Object.keys(fieldInfo).forEach(field => (initialConfig[field] = { name: field, ...fieldInfo[field], mode }));
  return initialConfig;
};

export const changeMode =
  (mode = "read") =>
  formConfig => {
    return Object.keys(formConfig).reduce((acc, key) => ({ ...acc, [key]: { ...formConfig[key], mode } }), {});
  };

export const changeModeForField = (field, mode) => formConfig => {
  return { ...formConfig, [field]: { ...formConfig[field], mode } };
};

export const checkEditEnabled = (isAddMode, formConfig) => isAddMode || Object.values(formConfig).some(({ mode }) => mode === "edit");

export const convertResponseDate = date => {
  if (!date) return null;
  if (typeof date === "string") {
    const [datePart] = date.split("T");
    return dayjs(datePart);
  }
  return dayjs(date);
};

export const formatPayloadDate = date => {
  if (!date) return null;
  if (!dayjs(date).isValid) return null;
  return dayjs(date).format(DEFAULT_DATE_PAYLOAD_FORMAT);
};

export const convertResponseDateTime = time => {
  if (!time) return null;
  const [h, m, s] = time.split(":");
  return dayjs().hour(+h).minute(+m).second(+s);
};

export const convertTimeToResponse = time => {
  if (!time) return null;
  return dayjs(time).isValid() 
    ? dayjs(time).format("HH:mm:ss") 
    : null;
};

export const lessThan16Years = value => {
  if (!value) return true;
  if (value.isValid && !value.isValid()) return "Enter valid date";
  const isOldEnough = dayjs().diff(dayjs(value), "years") >= 16;
  if (!isOldEnough) {
    warningToast("Age should be greater than 16 years");
    return "Age should be greater than 16 years";
  }
  return true;
};

export const convertFileToBase64 = async file => {
  if (!file) return null;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader?.result?.split(",")[1]);
    reader.onerror = error => reject(new Error(error));
    reader.readAsDataURL(file);
  });
};

export const convertFileToBase64Model = async file => {
  if (file === null || file === undefined) return null;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        base64String: reader?.result?.split(",")[1],
        fileName: file?.name
      });
    reader.onerror = error => reject(new Error(error));
    reader.readAsDataURL(file);
  });
};

export const hoursFormatter = hour => {
  if (hour === undefined || hour === null) return "-";
  return `${Math.floor(hour)} Hrs ${Math.round((hour - Math.floor(hour)) * 60)} Min`;
};

export const roundNumber = num => {
  if (!Number(num)) return 0;
  return Math.round(Number(num) * 100) / 100;
};

export const renderFormattedDate = date => {
  if (!date) return "";
  if (!dayjs(date).isValid) return "";
  return dayjs.utc(date).format(DEFAULT_DATE_FORMAT);
};

export const validateNegativeNumber = fieldName => value => {
  if (value === undefined || value === null || value === "") return true;
  if (Number(value) < 0) return `${fieldName} cannot be negative`;
  return true;
};

export const validatePositiveInteger = ({ min = 1, max } = {}) => value => {
  if (value === undefined || value === null || value === "") return true;
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) return "Only positive numeric integers are allowed";
  if (num < min) return `Value must be at least ${min}`;
  if (max !== undefined && num > max) return `Value must not exceed ${max}`;
  return true;
};

export const validateWageRateExpirationDate = value => {
  if (!value) return true;
  if (value.isValid && !value.isValid()) return "Enter a valid date";

  const tenYearsLater = dayjs().add(10, "year");
  if (value.isAfter(tenYearsLater)) return "Date cannot be more than 10 years in the future";
  return true;
};

export const downloadFile = url => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.click();
};

export const downloadFileWithFileName = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const base64ToBlob = base64String => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([byteNumbers], { type: "application/octet-stream" });
};

export const stringToNumber = str => {
  if (str === undefined || str === null || str === "") return null;
  const num = Number(str);
  return isNaN(num) ? null : num;
};

export const sortOptions = options => {
  return [...options].sort((a, b) => {
    if (a.value === "All" && b.value !== "All") return -1;
    if (a.value !== "All" && b.value === "All") return 1;
    return (a.label || "").localeCompare(b.label || "");
  });
};

const toCamelCase = str => {
  const specialCases = {
    "Labor Classification": "position",
    "Straight Hours": "regularHours",
    "Straight Pay Rate": "regularPayRate",
    "STATE TAX": "stateWithHoldingTax",
    "STATE WH TAX": "stateWithHoldingTax",
    "FED WH TAX": "fedWithHoldingTax",
    FICA: "fica",
    "Last Four Digit SSN": "lastFourDigitSSN",
    "Internal Id": "employeeInternalId",
    "Gross Amount Earned this Project": "grossAmountEarned",
    "Payment in Lieu of Fringe Benefits": "paymentInLieuOfBenefitCredit",
    "Health Insurance Fringe Credit Hourly Rate": "fringeHealthCareRate",
    "Retirement Fringe Credit Hourly Rate": "fringeRetirementRate",
    "Life Insurance Fringe Credit Hourly Rate": "fringeLifeInsuranceRate",
    "Vacation or Sick Time Fringe Credit Hourly Rate": "fringeSickTimeRate",
    "Holiday Time Fringe Credit Hourly Rate": "fringeHolidayRate",
    "Supplementary Unemployment Benefits Fringe Credit Hourly Rate": "fringeSupplementalUnemploymentRate",
    "Total Fringe Benefit Credit": "totalFringeBenefitCredit",
    "Apprentice Training Fringe Credit Hourly Rate": "fringeTrainingRate",
    "Amendment": "isAmendment",
    "Supplement": "isSupplement",
    "Is Union Employee?": "isUnion",
    "Health Insurance": "fringeHealthCareRate",
    "Retirement/Pension": "fringeRetirementRate",
    "Life Insurance": "fringeLifeInsuranceRate",
    "Vacation/Sick Time": "fringeSickTimeRate",
    "Holiday Time": "fringeHolidayRate",
    "Apprentice Training": "fringeTrainingRate",
    "Supplementary Unemployment Benefits": "fringeSupplementalUnemploymentRate",
    "Work Area":"workAreaName"
  };

  if (specialCases[str]) {
    return specialCases[str];
  }

  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) return "";
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const formatKeys = data => {
  return data?.map(item => {
    const formattedItem = {};
    for (const key in item) {
      const formattedKey = toCamelCase(key.trim());
      formattedItem[formattedKey] = item[key] == null ? "" : item[key];
    }
    return formattedItem;
  });
};

export const toBoolean = value => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
};

export const roundToTwoDecimals = (value) => {
  if (typeof value === "number") {
    return _.round(value, 2).toFixed(2);
  }
  return value;
};

// Returns true if valid, false if not
export const isDateApprenticeshipBeginsInRange = dateString => {
  if (!dateString) return true;
  const today = new Date();
  const dateBegin = new Date(dateString);
  const minDate = new Date(today);
  minDate.setFullYear(today.getFullYear() - 5);
  const maxDate = new Date(today);
  maxDate.setFullYear(today.getFullYear() + 20);
  return dateBegin >= minDate && dateBegin <= maxDate;
};

// Multi-document upload helpers (e.g. Wage Determination Documents)
export const ACCEPTED_FILE_EXTENSIONS = [".pdf", ".txt", ".doc", ".docx", ".png", ".jpg", ".jpeg"];

export const ACCEPTED_FILE_TYPES_LABEL = "PDF, TXT, DOC, PNG or JPEG";

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const MAX_LABEL_LENGTH = 50;

export const getFileExtension = (fileName = "") => {
  const parts = fileName.split(".");
  return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : "";
};

export const getFileIcon = fileName => {
  const extension = getFileExtension(fileName);

  switch (extension) {
    case ".pdf":
      return { Icon: PictureAsPdfIcon, colorClass: "file-row_icon-pdf" };
    case ".doc":
    case ".docx":
      return { Icon: ArticleIcon, colorClass: "file-row_icon-doc" };
    case ".png":
    case ".jpg":
    case ".jpeg":
      return { Icon: ImageIcon, colorClass: "file-row_icon-image" };
    case ".txt":
      return { Icon: DescriptionIcon, colorClass: "file-row_icon-txt" };
    default:
      return { Icon: InsertDriveFileIcon, colorClass: "file-row_icon-default" };
  }
};

export const formatFileSize = bytes => {
  if (bytes === null || bytes === undefined) return "Size unavailable";
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};


export const validateFile = file => {
  const extension = getFileExtension(file.name);

  if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `"${file.name}" is an invalid file format. Please upload a supported file type (${ACCEPTED_FILE_TYPES_LABEL}).`
    };
  }

  if (file.size === 0) {
    return { valid: false, error: `"${file.name}" is empty (0 KB) and cannot be uploaded.` };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB size limit.` };
  }

  return { valid: true, error: "" };
};

const formatUploadedBy = createdBy => {
  if (typeof createdBy === "string" && createdBy) return createdBy;
  if (typeof createdBy === "number") return `${createdBy}`;
  return "";
};

export const mapWageDeterminationFilesFromResponse = (apiFiles = []) => {

  const mapped = (apiFiles || []).map(apiFile => ({
    id: apiFile.projectWageDeterminationFileId,
    projectWageDeterminationFileId: apiFile.projectWageDeterminationFileId,
    name: apiFile.fileName,
    label: apiFile.description || "",
    originalLabel: apiFile.description || "",
    size: apiFile.fileSizeMb == null ? null : Math.round(apiFile.fileSizeMb * 1024 * 1024),
    uploadedDate: apiFile.createdDate
      ? new Date(apiFile.createdDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "",
    uploadedBy: formatUploadedBy(apiFile.createdBy),
    url: apiFile.path,
    isNew: false
  }));

  return mapped;
};

export const buildWageDeterminationSavePayload = async (files = []) => {
  const newFiles = (files || []).filter(file => file.isNew && file.file);

  const converted = await Promise.all(
    newFiles.map(async file => {
      const base64Model = await convertFileToBase64Model(file.file);
      return {
        file: {
          base64String: base64Model?.base64String || null,
          fileName: base64Model?.fileName || file.name
        },
        description: file.label || ""
      };
    })
  );
  return converted;
};

export const buildWageDeterminationDescriptionUpdates = (files = []) => {
  const updates = (files || [])
    .filter(file => !file.isNew && file.id && file.label !== file.originalLabel)
    .map(file => ({
      projectWageDeterminationFileId: file.id,
      description: file.label || ""
    }));
  return updates;
};